import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Link,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  styled,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUserId } from "@utils/userUtils";
import { getUserFacilities } from "@services/facilityManagement";
import type { UserFacility } from "@services/facilityManagement";
import { getCustomerAgreementMappings, FlattenedAgreement } from "@services/agreements";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import {
  filterFacilitiesEligibleForAgreements,
  getFacilityCustomerId,
  facilityLabel,
  resolveInitialFacility,
} from "@utils/agreementsFacilities";

/** Same title band as Profile / Regulations Library (Discovery 80px + 20px). */
const PAGE_TITLE_BAND_HEIGHT_PX = 100;

const PageContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
});

const HeaderContainer = styled(Box)({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  flexShrink: 0,
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const TitleBand = styled(Box)(({ theme }) => ({
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  padding: "0 64px 24px",
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 16px",
    height: "auto",
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

const HeaderTitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const HeaderIconWrapper = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const HeaderIcon = styled(DescriptionOutlined)({
  fontSize: "28px",
  color: "#065b70",
});

const HeaderSubtitleRow = styled(Box)(({ theme }) => ({
  padding: "0 64px 12px",
  marginTop: "-20px",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 12px",
    marginTop: "-12px",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: "1 0 auto",
  padding: "16px 48px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflow: "visible",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px 24px",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "28px",
  lineHeight: 1.5,
  letterSpacing: "-1px",
  color: "#262626",
  textAlign: "left",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#4b5563",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
}));

const HeaderRow = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1.4fr",
  columnGap: "12px",
  padding: "10px 14px",
  borderRadius: "10px",
  backgroundColor: "#e6fafc",
  border: "1px solid #99d4e1",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  color: "#033843",
}));

const Row = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1.4fr",
  columnGap: "12px",
  alignItems: "center",
  padding: "10px 12px",
  borderBottom: "1px solid #e5f5f9",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  color: "#111827",
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
}));

const StatusPill = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 10px",
  borderRadius: "999px",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
}));

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function getStatusLabel(value: boolean): { label: string; color: string; bg: string } {
  if (value) {
    return {
      label: "Accepted",
      color: "#166534",
      bg: "#dcfce7",
    };
  }
  return {
    label: "Pending",
    color: "#92400e",
    bg: "#fef3c7",
  };
}

type LocationState = { customerId?: number } | null | undefined;

const AgreementsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agreements, setAgreements] = useState<FlattenedAgreement[]>([]);
  const [eligibleFacilities, setEligibleFacilities] = useState<UserFacility[]>([]);
  /** User-facility row id (unique); customer id for API comes from the selected row. */
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eligibleFacilitiesRef = useRef<UserFacility[]>([]);
  eligibleFacilitiesRef.current = eligibleFacilities;

  const applyMappingsResult = useCallback(
    (customerId: number, items: FlattenedAgreement[]) => {
      const hasPending = items.some((a) => !a.acceptance_status);
      if (!items.length || hasPending) {
        navigate("/dashboard/agreements/acceptance", {
          replace: true,
          state: { customerId },
        });
        return;
      }
      setAgreements(items);
    },
    [navigate],
  );

  const loadFacilitiesAndMappings = useCallback(
    async (options?: { preferredCustomerId?: number | null }) => {
      setLoading(true);
      setError(null);
      try {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        if (!userId || Number.isNaN(userId)) {
          throw new Error("User is not available. Please sign in again.");
        }

        const facilitiesRes = await getUserFacilities(userId);
        const raw = Array.isArray(facilitiesRes.facilities) ? facilitiesRes.facilities : [];
        const facilities = filterFacilitiesEligibleForAgreements(raw);
        setEligibleFacilities(facilities);

        if (!facilities.length) {
          throw new Error("No facilities found for the current user.");
        }

        const initialFacility = resolveInitialFacility(
          facilities,
          options?.preferredCustomerId,
        );
        const customerId = initialFacility ? getFacilityCustomerId(initialFacility) : null;
        if (initialFacility == null || customerId == null) {
          throw new Error("Customer id is not available for the selected facility.");
        }

        setSelectedFacilityId(initialFacility.id);

        const result = await getCustomerAgreementMappings(1, 100, {
          customer_id: customerId,
        });
        const items = result.agreements || [];
        applyMappingsResult(customerId, items);
      } catch (err: any) {
        setError(err?.message || "Unable to load agreements.");
        setAgreements([]);
      } finally {
        setLoading(false);
      }
    },
    [applyMappingsResult],
  );

  const reloadMappingsForFacility = useCallback(
    async (facilityId: number) => {
      setLoading(true);
      setError(null);
      try {
        setSelectedFacilityId(facilityId);
        const row = eligibleFacilitiesRef.current.find((f) => f.id === facilityId);
        const customerId = row ? getFacilityCustomerId(row) : null;
        if (customerId == null) {
          throw new Error("Customer id is not available for the selected facility.");
        }
        const result = await getCustomerAgreementMappings(1, 100, {
          customer_id: customerId,
        });
        const items = result.agreements || [];
        applyMappingsResult(customerId, items);
      } catch (err: any) {
        setError(err?.message || "Unable to load agreements.");
        setAgreements([]);
      } finally {
        setLoading(false);
      }
    },
    [applyMappingsResult],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const preferred = (location.state as LocationState)?.customerId;
    void loadFacilitiesAndMappings({
      preferredCustomerId: typeof preferred === "number" ? preferred : undefined,
    });
  }, [loadFacilitiesAndMappings, location.key]);

  const hasAny = agreements.length > 0;

  return (
    <PageContainer>
      <HeaderContainer>
        <TitleBand
          style={{
            height: PAGE_TITLE_BAND_HEIGHT_PX,
            minHeight: PAGE_TITLE_BAND_HEIGHT_PX,
            padding: "0 64px 24px",
            display: "flex",
            alignItems: "flex-end",
            boxSizing: "border-box",
          }}
        >
          <HeaderTitleRow>
            <HeaderIconWrapper>
              <HeaderIcon />
            </HeaderIconWrapper>
            <Title>User Agreements</Title>
          </HeaderTitleRow>
        </TitleBand>
        <HeaderSubtitleRow>
          <Subtitle>
            List of all customer agreements for UniComply rules.
          </Subtitle>
        </HeaderSubtitleRow>
      </HeaderContainer>
      <ContentContainer>
        {eligibleFacilities.length > 0 && selectedFacilityId != null ? (
          <FormControl fullWidth size="small" sx={{ maxWidth: 560 }}>
            <InputLabel id="agreements-facility-label">Facility</InputLabel>
            <Select
              labelId="agreements-facility-label"
              label="Facility"
              value={selectedFacilityId}
              disabled={loading}
              renderValue={(selected) => {
                const facility = eligibleFacilities.find((f) => f.id === Number(selected));
                if (!facility) return "";
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <BusinessOutlined
                      sx={{ fontSize: 18, color: "#465a63", flexShrink: 0 }}
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {facilityLabel(facility)}
                    </span>
                  </Box>
                );
              }}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isNaN(v) && v !== selectedFacilityId) {
                  void reloadMappingsForFacility(v);
                }
              }}
            >
              {eligibleFacilities.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <BusinessOutlined
                      sx={{ fontSize: 18, color: "#465a63", flexShrink: 0 }}
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {facilityLabel(f)}
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {error ? (
          <Alert
            severity="error"
            sx={{ mt: 1, mb: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const preferred = (location.state as LocationState)?.customerId;
                  const row =
                    selectedFacilityId != null
                      ? eligibleFacilities.find((f) => f.id === selectedFacilityId)
                      : undefined;
                  const fallbackCid = row ? getFacilityCustomerId(row) : undefined;
                  void loadFacilitiesAndMappings({
                    preferredCustomerId:
                      typeof preferred === "number"
                        ? preferred
                        : typeof fallbackCid === "number"
                          ? fallbackCid
                          : undefined,
                  });
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : !hasAny ? (
          <Box
            sx={{
              mt: 1,
              borderRadius: "12px",
              border: "1px dashed #99d4e1",
              backgroundColor: "#f8fdfe",
              padding: "20px 16px",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#262626",
              }}
            >
              No agreements found
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: "Rethink Sans, sans-serif",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              There are currently no customer agreements to display.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 2,
              borderRadius: "12px",
              border: "1px solid #e5f5f9",
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            <HeaderRow>
              <span>Agreement name</span>
              <span>Status</span>
              <span>Effective date</span>
            </HeaderRow>
            <Box>
              {agreements.map((a) => {
                const status = getStatusLabel(a.acceptance_status);
                return (
                  <Row key={a.mapping_id}>
                    <Link
                      href={a.agreement_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: a.agreement_url ? "#0a829d" : "#111827",
                        pointerEvents: a.agreement_url ? "auto" : "none",
                        cursor: a.agreement_url ? "pointer" : "default",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={a.agreement_display_name}
                    >
                      {a.agreement_display_name}
                    </Link>
                    <Box>
                      <StatusPill sx={{ color: status.color, backgroundColor: status.bg }}>
                        {status.label}
                      </StatusPill>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "14px",
                        color: "#4b5563",
                      }}
                    >
                      {formatDate(a.start_date) || "—"}
                    </Typography>
                  </Row>
                );
              })}
            </Box>
          </Box>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default AgreementsPage;
